import { Outlet, Link, useRouterState } from "@tanstack/react-router";
import { useQuery, useMutation, Authenticated, Unauthenticated, AuthLoading } from "convex/react";
import { SignInButton, UserButton } from "@clerk/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";
import { api } from "../../convex/_generated/api";
import { navFor, navGroups, NAV_ICONS, type NavItem } from "../lib/nav";
import { useScrollEdges } from "../lib/useScrollEdges";
import { OrgSettingsContext } from "../lib/orgSettings";
import { ViewerContext } from "../lib/viewer";
import { ROLE_LABELS, type Role } from "@shared/roles";
import { Loading, ErrorState } from "../components/states";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

/**
 * The app shell.
 *
 * One navigation system per viewport, never two.
 *
 *   <lg   the phone. A bottom tab bar carries the four primary destinations
 *         (thumb-reachable, 56px, unchanged in tap cost), and the rest sit in a
 *         quiet strip under the header. Content column stays `max-w-3xl`.
 *   lg+   the bottom bar and the strip both disappear. The registry's
 *         `<Sidebar collapsible="icon">` lists *every* destination the role
 *         has, filed under headings, and the content column opens to
 *         `max-w-6xl` so a screen can lay out three or four columns instead of
 *         stacking a phone layout down the middle.
 *
 * Nothing here decides what a user may see — it decides what is easy to reach.
 * The permission boundary is in convex/lib/authz.ts and is enforced there.
 *
 * **The two navs are mounted, not merely hidden.** Rendering both and hiding
 * one with `lg:hidden` puts every destination's label in the DOM twice, and the
 * harness drives this app by accessible name: `waitForSelector("text=/Approvals/")`
 * resolves to the *first* match in document order, which — the sidebar being
 * above `<main>` — was an invisible link, so the wait hung for twenty seconds
 * and took a demo checkbox down with it. A duplicated label is a real bug even
 * when a person cannot see it, so the viewport decides what exists rather than
 * what is painted.
 *
 * That rule survives the move to the registry sidebar unchanged, and it is why
 * `<SidebarProvider>` itself is inside the `isDesktop` branch: the registry
 * component has a mobile <Sheet> arm of its own, and mounting the provider on a
 * phone would put a second copy of every label one state flag away from the DOM.
 * Below `lg` there is no sidebar tree at all.
 */

/**
 * One `MediaQueryList` for the app, built once at module load.
 *
 * `getSnapshot` runs on every render and `subscribe` on every mount, and
 * calling `window.matchMedia` in each of them built two independent listener
 * lists for the same query — so the store was subscribed to one object and
 * reading its answer off another. One object, both jobs.
 */
const DESKTOP_QUERY = "(min-width: 1024px)"; /* Tailwind's `lg` */
const desktop = window.matchMedia(DESKTOP_QUERY);

const subscribeToDesktop = (onChange: () => void) => {
  desktop.addEventListener("change", onChange);
  return () => desktop.removeEventListener("change", onChange);
};

/**
 * Which navigation exists right now.
 *
 * `useSyncExternalStore` rather than a `useEffect` + `useState` pair: it reads
 * the match during the first render, so there is no frame where a phone has a
 * sidebar in its DOM. This is a client-rendered SPA, so the server snapshot is
 * never consulted; it answers `false` because the phone is the primary
 * viewport and the wrong guess should be the desktop one.
 */
const useIsDesktop = (): boolean =>
  useSyncExternalStore(
    subscribeToDesktop,
    () => desktop.matches,
    () => false,
  );

/**
 * The rail's two widths, in the shell rather than in the registry defaults.
 *
 * `RAIL` must equal `--nav-rail` in `src/index.css`, which is *not* just this
 * sidebar's width: `<ActionBar>` in `kit.tsx` offsets itself by
 * `calc(var(--nav-rail) + 1.5rem)` so a screen's fixed footer lines up with the
 * content column rather than running under the navigation. The registry's own
 * default is 16rem; 15rem is this app's number and the one that other file
 * already trusts. `RAIL_ICON` is the registry's `--sidebar-width-icon`, and
 * `<AppFrame>` republishes whichever of the two is current as `--nav-rail`, so
 * collapsing the rail moves the action bars with it.
 */
const RAIL = "15rem";
const RAIL_ICON = "3rem";

/** Just enough of `users.me` for the chrome to render. */
type Account = { role: Role; clinicName?: string | null };

/** The brand lockup. Same mark on the signed-out card, in the phone header and
 *  at the head of the desktop rail, so the first screen a client sees and every
 *  screen after it agree. */
function Wordmark({ size = "sm" }: { size?: "sm" | "lg" }) {
  const large = size === "lg";
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-lg bg-primary font-bold text-primary-foreground",
        large ? "size-14 rounded-2xl text-2xl" : "size-8 text-sm",
      )}
      aria-hidden="true"
    >
      N
    </span>
  );
}

function SignedOut() {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-6">
      <Card className="text-center">
        <CardHeader className="justify-items-center">
          <div className="mb-3 flex justify-center">
            <Wordmark size="lg" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">Nexora</CardTitle>
          <CardDescription>
            Supply, inventory and billing for the five clinics.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Clerk's `SignInButton` clones its child and injects the handler
              onto it, so the child has to be something that spreads whatever it
              is handed straight through to the DOM. The registry `Button` does
              — `...props` — which is the whole reason this is the registry
              component and not a wrapper with a closed prop list: a prop a
              wrapper does not name is a prop it drops, and the one being
              dropped here would be the click that signs Gloria in, failing
              silently on the first screen of the demo. */}
          <SignInButton mode="modal">
            <Button type="button" size="lg" className="w-full">
              Sign in
            </Button>
          </SignInButton>
          <p className="mt-4 text-xs text-muted-foreground">
            We email you a code. There is no password to remember.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * A signed-in Clerk account with no matching staff record.
 *
 * Names the address Clerk actually sent, because "no account set up" without
 * the address is unactionable — and the single most common cause is signing in
 * with a different address from the one on the staff list.
 */
function NotProvisioned() {
  const diagnostics = useQuery(api.users.signInDiagnostics);

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-6">
      <div className="mb-6 flex items-center gap-2">
        <Wordmark />
        <span className="font-semibold tracking-tight">Nexora</span>
      </div>
      <ErrorState
        title="No account set up for this email"
        body={
          diagnostics?.email
            ? `${diagnostics.email} is not on the staff list. An owner can add it under Staff, then sign in again.`
            : "This account is not on the staff list yet. An owner can add it under Staff, then sign in again."
        }
      />
      {diagnostics && !diagnostics.email ? (
        <p className="mt-4 rounded-xl bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-amber-300">
          Sign-in token carries no email address, so we cannot match you to a
          staff record. Claims present: {diagnostics.claimKeys.join(", ") || "none"}.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Honest, and quiet.
 *
 * It was a full-bleed amber-400 strip in bold, which made a disclaimer the
 * loudest object on every screen and the first thing in every screenshot — a
 * client rehearsal flagged it on sight. The fact still has to be stated (the
 * emails really are redirected, and Dee will ask), so it stays at the very top,
 * on every screen, undismissable. It is one 11px line on a muted fill with a
 * 2px amber rule for the eye to catch: readable when looked for, never brighter
 * than the page's primary action.
 *
 * The wording is unchanged and is not ours to change.
 */
function DemoBanner() {
  return (
    <div className="bg-muted px-4 py-1">
      <p className="mx-auto flex max-w-3xl items-center gap-2 text-[11px] leading-5 text-muted-foreground lg:max-w-6xl">
        <span
          aria-hidden="true"
          className="h-3 w-0.5 shrink-0 rounded-full bg-amber-400"
        />
        Demo data — invoice emails are redirected, not sent to clinics
      </p>
    </div>
  );
}

/** Who is signed in, and where they stand. The same block in both headers. */
function AccountBadge({ me }: { me: Account }) {
  return (
    <div className="ml-auto flex items-center gap-3">
      <div className="text-right leading-tight">
        <div className="text-xs font-medium">
          {me.clinicName ?? ROLE_LABELS[me.role]}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {me.clinicName ? ROLE_LABELS[me.role] : "All clinics"}
        </div>
      </div>
      <UserButton />
    </div>
  );
}

/**
 * The desktop rail: the registry sidebar, driven by the same `navGroups` the
 * app has always used.
 *
 * `role="navigation"` + `aria-label="Sections"` on `<SidebarContent>` rather
 * than a `<nav>` wrapper of our own. The registry composes this region out of
 * plain divs, and wrapping it would mean re-declaring its flex geometry
 * somewhere the registry cannot see; spreading the two attributes onto the
 * element it already renders keeps the landmark — and the exact label the
 * previous rail carried — with no structural deviation at all.
 */
function AppSidebar({
  sections,
  pathname,
}: {
  sections: Array<{ heading: string | null; items: NavItem[] }>;
  pathname: string;
}) {
  return (
    /* `z-40`, not the registry's `z-10`. The layer table in index.css puts the
       navigation above a screen's own <ActionBar> (`z-30`), which is fixed
       across the full width of the viewport — at `z-10` its translucent
       background would paint over the foot of the rail. */
    <Sidebar collapsible="icon" className="z-40">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <Wordmark />
                <span className="truncate font-semibold tracking-tight">
                  Nexora
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent role="navigation" aria-label="Sections">
        {sections.map((section) => (
          <SidebarGroup key={section.heading ?? "top"}>
            {section.heading ? (
              <SidebarGroupLabel>{section.heading}</SidebarGroupLabel>
            ) : null}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const active = pathname === item.to;
                  const Icon = NAV_ICONS[item.to];
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={item.label}
                      >
                        <Link
                          to={item.to}
                          aria-current={active ? "page" : undefined}
                        >
                          {Icon ? <Icon aria-hidden="true" /> : null}
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}

/**
 * Everything to the right of the rail: header, banner, content column.
 *
 * A component of its own because it has to be *inside* `<SidebarProvider>` to
 * read `useSidebar()`, and the one thing it needs from there is the rail's
 * current width — republished as `--nav-rail` so `<ActionBar>`'s offset (see
 * `kit.tsx`) follows the rail when it collapses to icons instead of leaving a
 * screen's footer buttons stranded 12rem to the right.
 *
 * `<SidebarInset>` is the registry's `<main>`, which is why the content column
 * below it is a `<div>`: one `<main>` per document, and `.app-main` is a class,
 * not an element.
 */
function AppFrame({
  me,
  showBanner,
  children,
}: {
  me: Account;
  showBanner: boolean;
  children: ReactNode;
}) {
  const { state } = useSidebar();

  return (
    <SidebarInset
      style={
        {
          "--nav-rail": state === "collapsed" ? RAIL_ICON : RAIL,
        } as CSSProperties
      }
    >
      {showBanner ? <DemoBanner /> : null}

      {/* `h-16` is load-bearing beyond this file: `--header-h` in index.css is
          4rem, and Dispatch and Request both hang sticky desktop columns off
          it. `useHeaderOffset` measures this element directly, so a screen's
          own sticky toolbar tracks it whatever it ends up being. */}
      <header className="sticky top-0 z-50 flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4 lg:px-8">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mr-2 data-[orientation=vertical]:h-4"
        />
        <AccountBadge me={me} />
      </header>

      {/* The registry dashboard's contrast: a muted page under white cards.
          It goes on the region rather than on `<body>` (which Phase 1 made
          `bg-background`) so the rail stays white against it and every screen's
          stock <Card> has something to sit on. */}
      <div className="flex-1 bg-muted/50">
        <div className="app-main mx-auto w-full max-w-6xl px-8 pt-8">
          {children}
        </div>
      </div>
    </SidebarInset>
  );
}

/**
 * The phone: header, one scrolling column, a bottom tab bar.
 *
 * Restyled onto the registry's tokens, but structurally the bar it has always
 * been — same destinations, same overflow strip, same 56px targets. A hamburger
 * sheet would cost a dental assistant a tap on every navigation, and the
 * two-tap constraint is measured, not asserted (`tools/clickcount.mjs`).
 */
function PhoneFrame({
  me,
  showBanner,
  primary,
  secondary,
  pathname,
  children,
}: {
  me: Account;
  showBanner: boolean;
  primary: NavItem[];
  secondary: NavItem[];
  pathname: string;
  children: ReactNode;
}) {
  const strip = useScrollEdges();
  const stripNode = useRef<HTMLElement | null>(null);
  const centredFor = useRef<string | null>(null);

  /**
   * Bring the current destination into view in the secondary strip.
   *
   * The strip scrolls horizontally and starts at its left edge, so on the
   * owner's phone "Pricing rules" — the seventh item, and the one they are
   * standing on — rendered half under the right-hand fade, reading as
   * "Pricing ru". Centring it means the strip answers "where am I" before it
   * answers "where else could I go".
   *
   * Once per pathname, tracked in a ref rather than run on every render: a
   * live Convex update must not yank the strip back while someone is scrolling
   * it. Called from both the ref callback and an effect because neither alone
   * is enough — the strip does not exist on the first render (the shell is
   * still waiting on `users.me`), so an effect keyed on the pathname would
   * find nothing and never run again for that route.
   */
  const centreActive = useCallback(
    (nav: HTMLElement | null) => {
      if (!nav || centredFor.current === pathname) return;
      const active = nav.querySelector<HTMLElement>('[aria-current="page"]');
      if (!active) return;
      centredFor.current = pathname;
      nav.scrollLeft = Math.max(
        0,
        active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2,
      );
    },
    [pathname],
  );

  useEffect(() => {
    centreActive(stripNode.current);
  }, [centreActive]);

  return (
    <div className="flex min-h-dvh flex-col">
      {showBanner ? <DemoBanner /> : null}

      {/* Opaque `bg-background`, not a translucent blur: cards scrolling under
          a see-through header read as content overlapping the navigation on the
          Overview and Invoices captures. */}
      <header className="pt-safe sticky top-0 z-50 border-b bg-background">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-3 px-4 pb-3">
          <Link to="/" className="flex items-center gap-2 rounded-lg">
            <Wordmark />
            <span className="font-semibold tracking-tight">Nexora</span>
          </Link>
          <AccountBadge me={me} />
        </div>

        {secondary.length > 0 ? (
          <div className="relative mx-auto max-w-3xl">
            <nav
              aria-label="Secondary"
              ref={(node) => {
                strip.setNode(node);
                stripNode.current = node;
                centreActive(node);
              }}
              className="overflow-x-auto px-4 pb-2"
            >
              <ul className="flex gap-1">
                {secondary.map((item) => {
                  const active = pathname === item.to;
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "block rounded-md px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors",
                          active
                            ? "bg-accent text-primary"
                            : "text-muted-foreground active:bg-accent",
                        )}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Only where there is genuinely more to reach. Sized to cover a
                clipped word rather than to decorate the edge. */}
            {!strip.atStart ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent"
              />
            ) : null}
            {!strip.atEnd ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background via-background/80 to-transparent"
              />
            ) : null}
          </div>
        ) : null}
      </header>

      {/* `.app-main` owns the bottom clearance for every screen — the tab bar
          plus whatever height the screen's own <ActionBar> reports (see
          index.css). It used to be a flat `pb-36` here *and* a `pb-actionbar`
          guess on three individual screens; two systems for one problem, and
          the screen that got it wrong shipped an untappable button. One
          mechanism now, and no number to keep in step with a bar's contents. */}
      <div className="flex-1 bg-muted/50">
        <main className="app-main mx-auto w-full max-w-3xl px-4 py-5">
          {children}
        </main>
      </div>

      {/* Opaque for the same reason as the header: at `bottom-16` a screen's
          own action bar sits inside this bar's 77px, and a translucent tab bar
          let it bleed through. */}
      <nav
        aria-label="Primary"
        className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t bg-background pt-1"
      >
        <ul className="mx-auto flex max-w-3xl">
          {primary.map((item) => {
            const active = pathname === item.to;
            return (
              /* `min-w-0` + `truncate`: four labels across 390px leaves ~97px
                 each, and "Approvals" plus its padding does not fit. It was
                 being sliced mid-word, which reads as breakage; an ellipsis
                 reads as a long word. The label text itself is unchanged —
                 the harness matches on it. */
              <li key={item.to} className="min-w-0 flex-1">
                <Link
                  to={item.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex min-h-14 items-center justify-center px-1 text-center text-sm font-medium transition-colors",
                    active
                      ? "font-semibold text-primary"
                      : "text-muted-foreground",
                  )}
                >
                  <span className="max-w-full truncate px-2.5 py-2">
                    {item.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}

function AppChrome() {
  const me = useQuery(api.users.me);
  const settings = useQuery(api.users.settings);
  const link = useMutation(api.users.linkIdentity);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isDesktop = useIsDesktop();

  /**
   * The org's display settings, published once for every pill below.
   *
   * `backOrderUsesOwnColour` is an org-wide answer, and threading it down to
   * each `StatusPill` by hand meant a screen that forgot rendered back-order
   * red while the screen beside it rendered it amber. The shell already has the
   * query; the pills read it from here unless they are handed something else.
   */
  const org = useMemo(
    () => ({
      backOrderUsesOwnColour: settings?.backOrderUsesOwnColour ?? false,
    }),
    [settings?.backOrderUsesOwnColour],
  );

  // Bind the Clerk account to its seeded staff row on first sign-in. The email
  // comes from the verified token on the server; this only triggers the check.
  useEffect(() => {
    if (me === null) void link({});
  }, [me, link]);

  if (me === undefined) {
    return (
      <div className="mx-auto max-w-md p-4">
        <Loading rows={4} label="Signing you in" />
      </div>
    );
  }

  if (me === null) return <NotProvisioned />;

  const items = navFor(me.role);
  const primary = items.filter((item) => item.primary).slice(0, 4);
  const sections = navGroups(me.role);

  /**
   * Everything the bottom bar did not take, in sidebar-group order.
   *
   * Derived from what `primary` actually rendered rather than from the
   * `primary` flag, so a fifth flagged destination falls into the strip instead
   * of disappearing off the end of the `slice(0, 4)`. Group order also makes
   * the strip read as something ("Dispatch, Inventory, Alerts, Statements,
   * Pricing rules, Clinics, Staff") rather than as declaration order.
   */
  const onBottomBar = new Set(primary.map((item) => item.to));
  const secondary = sections
    .flatMap((section) => section.items)
    .filter((item) => !onBottomBar.has(item.to));

  const showBanner = Boolean(settings?.demoMode);

  /**
   * Both providers hang off the same fact, at the same point.
   *
   * Everything above this line has already established that `me` is resolved
   * and non-null — `undefined` returned the skeleton, `null` returned
   * `<NotProvisioned>` — so this is the one place in the app where that is
   * known. Publishing it means a screen below can simply *have* the viewer
   * rather than re-subscribing to `users.me` and re-writing the two guards; see
   * `src/lib/viewer.ts` for the contract.
   */
  return (
    <ViewerContext.Provider value={me}>
      <OrgSettingsContext.Provider value={org}>
        {isDesktop ? (
          <SidebarProvider
            style={
              {
                "--sidebar-width": RAIL,
                "--sidebar-width-icon": RAIL_ICON,
              } as CSSProperties
            }
          >
            <AppSidebar sections={sections} pathname={pathname} />
            <AppFrame me={me} showBanner={showBanner}>
              <Outlet />
            </AppFrame>
          </SidebarProvider>
        ) : (
          <PhoneFrame
            me={me}
            showBanner={showBanner}
            primary={primary}
            secondary={secondary}
            pathname={pathname}
          >
            <Outlet />
          </PhoneFrame>
        )}
      </OrgSettingsContext.Provider>
    </ViewerContext.Provider>
  );
}

export function Shell() {
  return (
    <>
      <AuthLoading>
        <div className="mx-auto max-w-md p-6">
          <Loading rows={3} label="Checking your session" />
        </div>
      </AuthLoading>
      <Unauthenticated>
        <SignedOut />
      </Unauthenticated>
      <Authenticated>
        <AppChrome />
      </Authenticated>
    </>
  );
}
